"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getMonitoredTokenData = exports.startTokenMonitor = exports.TokenMonitor = void 0;
const errors_1 = require("../errors");
const events_1 = require("../events");
const logging_1 = require("../logging");
const DEFAULT_EXPIRATION_BUFFER_SECONDS = 60 * 5; // 5 minutes
/** Token monitor. Monitors current auth token for expiration and will alert as it nears expiration based on configured threshold. */
class TokenMonitor {
    constructor(config, adapter) {
        this.tokenWatchers = []; // prior experience has taught me to handle the possibility of multiple being set
        /** Use to alert that auth token is expiring. */
        this.alertExpiring = () => {
            this.clearWatchers();
            if (this.isTokenExpired(this.currentToken)) {
                // safety check in case token managed to expire before watcher fired
                return this.alertExpired();
            }
            events_1.authTokenExpiring.notify(this.currentToken);
            // set up to watch for expiration (if we get this far, we have a token cuz it's checked in isTokenExpired)
            const msToAlertIn = this.currentToken.expirationTime - Date.now();
            (0, logging_1.verbose)(`Starting expiry auth token watcher. Will alert in ${msToAlertIn}ms.`, this.currentToken);
            this.tokenWatchers.push(window.setTimeout(this.alertExpired.bind(this), msToAlertIn));
        };
        /** Use to alert that auth token has expired. */
        this.alertExpired = () => {
            this.clearWatchers();
            if (this.isTokenExpired(this.currentToken)) {
                // safety check in case token was refreshed before watcher fired
                events_1.authTokenExpired.notify(this.currentToken);
            }
        };
        /** Gets current token data. */
        this.getCurrentToken = async () => {
            if (this.shouldUseCurrentToken()) {
                return { ok: true, data: this.currentToken };
            }
            const getTokenResult = await this.tryGetCurrentToken();
            if (!getTokenResult.data) {
                this.clearWatchers();
                this.currentToken = undefined;
                return getTokenResult;
            }
            const isSameToken = this.currentToken && getTokenResult.data.rawToken === this.currentToken.rawToken;
            if (!isSameToken) {
                this.updateToken(getTokenResult.data);
            }
            return getTokenResult;
        };
        this.config = config;
        this.adapter = adapter;
        this.tokenBuffer = config.authentication.tokenExpirationWarningThreshold || DEFAULT_EXPIRATION_BUFFER_SECONDS;
        events_1.authTokenUpdated.subscribe(this.updateToken.bind(this));
    }
    /**
     * Starts monitoring the current auth token for expiration.
     *
     * @returns {boolean} True if monitoring started, false if not.
     */
    start() {
        this.clearWatchers();
        if (!this.currentToken) {
            const message = 'Will not start monitoring auth token because it is not available. Be sure to call `getCurrentToken` first.';
            (0, logging_1.warn)(message);
            return false;
        }
        if (this.isTokenExpired(this.currentToken)) {
            const message = 'Will not start monitoring auth token because it has already expired.';
            (0, logging_1.warn)(message, this.currentToken);
            this.alertExpiring();
            return false;
        }
        const msToAlertIn = this.timeToGetNewToken - Date.now();
        if (msToAlertIn <= 0) {
            const message = 'Will not start monitoring auth token because it is already about to expire.';
            (0, logging_1.warn)(message, msToAlertIn, this.currentToken);
            this.alertExpiring();
            return false;
        }
        (0, logging_1.verbose)(`Starting expiry threshold auth token watcher. Will alert in ${msToAlertIn}ms. Buffer: ${this.tokenBuffer}`, this.currentToken);
        this.tokenWatchers.push(window.setTimeout(this.alertExpiring.bind(this), msToAlertIn));
        return true;
    }
    /** Gets if the given token has expired. */
    isTokenExpired(token) {
        return !token || Date.now() >= token.expirationTime;
    }
    clearWatchers() {
        this.tokenWatchers.forEach(window.clearTimeout);
        this.tokenWatchers = [];
    }
    /** Token updated handler. Will reset instance token and re-start watcher. */
    updateToken(token) {
        this.currentToken = token;
        this.start();
    }
    /** Time from now to start checking for new token, based on the configured threshold buffer. */
    get timeToGetNewToken() {
        if (!this.currentToken) {
            return Date.now();
        }
        const timeToGetNewToken = this.currentToken.expirationTime - this.tokenBuffer * 1000;
        return timeToGetNewToken;
    }
    /** Gets if instance token data should be used as is--based on its presence and nearness to its expiration. */
    shouldUseCurrentToken() {
        return this.timeToGetNewToken > Date.now();
    }
    /** Tries to get token data from configured adapter. */
    async tryGetCurrentToken() {
        try {
            return await this.adapter.getAuthTokenData();
        }
        catch (ex) {
            const message = 'Unexpected error getting auth token data.';
            (0, logging_1.error)(message, ex);
            return (0, errors_1.errorResult)('UNKNOWN', message, ex);
        }
    }
}
exports.TokenMonitor = TokenMonitor;
let tokenMonitor;
/** Start token monitor. Initiates getting current token and watching for expiration. */
const startTokenMonitor = async (config, adapter) => {
    tokenMonitor = new TokenMonitor(config, adapter);
    return await tokenMonitor.getCurrentToken(); // kick off token retrieval and watching
};
exports.startTokenMonitor = startTokenMonitor;
/**
 * Get the auth token data for the current user.
 *
 * @returns - result including the auth token data, if successful
 */
const getMonitoredTokenData = async () => {
    if (!tokenMonitor) {
        // should not get here under normal use via exported functions but guarding just in case
        return (0, errors_1.errorResult)('NOT_INITIALIZED', 'Auth utility not initialized. Call `initialize` first.');
    }
    return await tokenMonitor.getCurrentToken();
};
exports.getMonitoredTokenData = getMonitoredTokenData;
